import React from "react";
import { LuCheck, LuExternalLink, LuPlus } from "react-icons/lu";
import { cn } from "../../components/ui";
import { t } from "../../i18n";
import { Referencia } from "../../types/resources.types";
import { referenceUrl } from "./useReferences";

interface ReferenceItemProps {
  item: Referencia;
  selected: boolean;
  opened: boolean;
  onToggle: (id: number) => void;
  onOpen: (url: string) => void;
}

const CHIP =
  "inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-strong px-3 text-label font-semibold transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus sm:flex-none";

/** Uma referência sugerida: dados bibliográficos, "Abrir link" (vira "Conferido") e "Incluir"/"Incluída". */
export const ReferenceItem: React.FC<ReferenceItemProps> = ({ item, selected, opened, onToggle, onOpen }) => {
  const url = referenceUrl(item);
  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-control border border-line px-3.5 py-3 transition-opacity duration-fast sm:flex-row",
        !selected && "bg-surface-muted opacity-75"
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1 text-caption text-fg">
        <p>
          <span className="text-fg-subtle">{item.id}.</span> {item.autores} ({item.ano}).{" "}
          <strong className="font-semibold">{item.titulo}</strong>
        </p>
        <p className="break-words text-fg-muted">
          {[item.fonte, item.base, item.doi && `DOI ${item.doi}`].filter(Boolean).join(" · ")}
        </p>
        {item.resumo && <p>{item.resumo}</p>}
        {item.citacao_abnt && (
          <p className="mt-0.5 break-words border-t border-dashed border-line pt-1.5 text-fg-subtle">
            <b>{t("references.review.abnt")}</b> {item.citacao_abnt}
          </p>
        )}
      </div>
      <div className="flex shrink-0 gap-2 sm:w-36 sm:flex-col">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onOpen(url)}
            className={cn(CHIP, opened ? "border-success bg-success-soft text-success" : "border-primary-line bg-surface text-primary hover:bg-primary-tint")}
          >
            {opened ? <LuCheck size={15} aria-hidden /> : <LuExternalLink size={15} aria-hidden />}
            {t(opened ? "references.review.checked" : "references.review.openLink")}
          </a>
        ) : (
          <span className="self-center text-caption text-fg-subtle">{t("references.review.noLink")}</span>
        )}
        <button
          type="button"
          aria-pressed={selected}
          onClick={() => onToggle(item.id)}
          className={cn(CHIP, selected ? "border-primary bg-primary text-on-primary" : "border-primary-line bg-surface text-primary hover:bg-primary-tint")}
        >
          {selected ? <LuCheck size={15} aria-hidden /> : <LuPlus size={15} aria-hidden />}
          {t(selected ? "references.review.included" : "references.review.include")}
        </button>
      </div>
    </article>
  );
};
